import React, {
  useContext,
  useState,
  useRef,
  useCallback,
  RefObject,
} from 'react';
import {
  CellContext,
  ColumnDef,
} from '@tanstack/react-table';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  EditableRendererProps,
  EditableTableMeta,
} from 'core/components/VirtualTable/types';
import {
  VisitNoteBillingCodeModifierDTO,
} from 'dtos/visitNoteBillingCodeModifierDTO';
import {
  ModifierListSettingDTO,
} from 'dtos/modifierListSettingDTO';
import {
  ReadonlyContext,
  ReadonlyContextProps,
} from 'core/context/readonly';
import ToolTip from 'core/components/Tooltip';
import Button from 'pages/Dashboard/components/Button';
import IconButton from 'core/components/IconButton';
import AddIcon from '@mui/icons-material/Add';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import Pill from 'core/components/Pill';
import Popover from 'core/components/Popover';
import ModifierLookup from 'views/EMR/components/ModifierLookup';
import {
  useGetModifierList,
} from 'views/EMR/components/ServiceLookup/hooks';
import {
  getModifierSuggestions,
  ModifierSuggestion,
} from 'views/EMR/components/ServiceLookup/modifierSuggestionRules';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import uniqueId from 'lodash/uniqueId';
import defer from 'lodash/defer';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';

type ModifierActionProps = {
  suggestion?: ModifierSuggestion;
  modifierList: ModifierListSettingDTO[];
  onSelect: (modifier: VisitNoteBillingCodeModifierDTO) => void;
  onAddClick: () => void;
  buttonRef: RefObject<HTMLButtonElement | null>;
};

function ModifierAction({
  suggestion,
  modifierList,
  onSelect,
  onAddClick,
  buttonRef,
}: ModifierActionProps) {
  const fromList = modifierList.find(
    (modifier) => modifier.code?.toLowerCase() === suggestion?.code?.toLowerCase(),
  );

  return (
  <div
    className={clsx('flex items-center', {
      'h-5 gap-0.5 rounded-full border border-dashed border-blue-400 pr-1': !isNil(suggestion),
    })}
  >
    <ToolTip title="Add Modifier">
      <span className="flex">
        <Button
          ref={buttonRef}
          className="!font-medium !border-dashed !rounded-full !p-0 !w-5 !h-5 !-ml-px !min-w-[initial] !max-w-none !flex !items-center !justify-center !border-blue-500 !bg-white"
          color="primary"
          type="button"
          variant="outlined"
          onClick={onAddClick}
        >
          <AddIcon className="!w-3.5 !h-3.5" />
        </Button>
      </span>
    </ToolTip>
    {!isNil(suggestion) && (
      <ToolTip
        disableFocusListener={false}
        title={(
          <div className="flex flex-col gap-1 max-w-[400px]">
            <span className="font-semibold">{suggestion.label}</span>
            <span>{suggestion.description}</span>
          </div>
        )}
      >
        <span className="flex">
          <IconButton
            aria-label={suggestion.label}
            className="!p-0 !w-4 !h-4"
            onClick={() => {
              onSelect({
                code: suggestion.code,
                description: fromList?.description ?? suggestion.code,
              });
            }}
          >
            <BoltOutlinedIcon className="!w-4 !h-4 !text-blue-500 rotate-12" />
          </IconButton>
        </span>
      </ToolTip>
    )}
  </div>
);
}

type EditableModifiersProps = EditableRendererProps<VisitNoteBillingCodeModifierDTO[]> & {
  serviceCode?: string | null;
};

function EditableModifiers({
  value: modifiers = [],
  onChange,
  serviceCode,
}: EditableModifiersProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastPillRef = useRef<HTMLDivElement>(null);
  const [
    anchorEl,
    setAnchorEl,
  ] = useState<HTMLElement | null>(null);

  const { readonly } = useContext<ReadonlyContextProps>(ReadonlyContext);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { modifierList } = useGetModifierList();
  const suggestions = getModifierSuggestions({
    serviceCode,
    selectedModifierCodes: modifiers.map((modifier) => modifier.code),
  });

  const closePopover = () => {
    setAnchorEl(null);
  };

  const handleChange = useCallback((newValue: VisitNoteBillingCodeModifierDTO) => {
    if (notEmptyString(newValue?.code)) {
      const updatedModifiers = [...modifiers, newValue];
      onChange?.(updatedModifiers);
      closePopover();
      if (updatedModifiers.length === 4) {
        defer(() => {
          lastPillRef?.current?.focus();
        });
      }
    }
  }, [modifiers]);

  return (
    <div className="flex flex-col gap-2" ref={containerRef}>
      {modifiers?.map(({ code, description }, index) => notEmptyString(code) && (
        <ToolTip
          key={uniqueId(`${code}-tooltip`)}
          title={description}
          hide={isEmptyString(description)}
        >
          <span>
            <Pill
              ref={index === modifiers.length - 1 ? lastPillRef : undefined}
              key={uniqueId(`${code}-pill`)}
              id={code ?? ''}
              label={code ?? ''}
              disabled={readonly}
              onRemove={readonly ? undefined : () => {
                const newValue = [
                  ...modifiers.slice(0, index),
                  ...modifiers.slice(index + 1),
                ];
                onChange?.(newValue);
              }}
              classes={{
                root: clsx('!h-5', {
                  '!min-w-[32px] !items-center': readonly,
                  '!pr-1': !readonly,
                }),
                label: 'font-normal',
                icon: '!text-red-400',
              }}
              defaultColor="!bg-blue-200"
            />
          </span>
        </ToolTip>
      ))}
      {(modifiers?.length < 4 && !readonly) && (
        <ModifierAction
          suggestion={suggestions[0]}
          modifierList={modifierList}
          onSelect={handleChange}
          buttonRef={buttonRef}
          onAddClick={() => {
            buttonRef?.current?.focus();
            setAnchorEl(containerRef?.current);
          }}
        />
      )}
      {(modifiers.length === 0 && readonly) && '--'}
      {!isNil(anchorEl) && (
        <Popover
          anchorEl={anchorEl}
          onClose={() => { setAnchorEl(null); }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          classes={{ paper: '!w-64' }}
        >
          <ModifierLookup
            value={{ code: '' }}
            onChange={handleChange}
            onClose={closePopover}
            suggestions={suggestions}
          />
        </Popover>
      )}
    </div>
  );
}

export default {
  id: 'modifiers',
  accessorKey: 'modifiers',
  header: 'Modifiers',
  minSize: 80,
  maxSize: 160,
  cell: ({
    row,
    column,
    table,
    getValue,
  }: CellContext<VisitNoteServiceCode, unknown>) => {
    const { updateData } = table.options.meta as EditableTableMeta<VisitNoteServiceCode>;
    const value = getValue<VisitNoteBillingCodeModifierDTO[]>();

    return (
      <EditableModifiers
        value={value}
        serviceCode={row.original.code}
        onChange={(newValue) => {
          updateData?.(row.index, column.id, newValue, row?.original);
        }}
      />
    );
  },
} as ColumnDef<VisitNoteServiceCode>;
